import { addMonths } from "@vulpari/core";
import { useState } from "react";

import { useStore } from "../store.js";
import { AddNodeMenu } from "./AddNodeMenu.js";
import { ExportMenu } from "./ExportMenu.js";
import { Icon } from "./Icon.js";
import { LedgerMenu } from "./LedgerMenu.js";
import { SettingsPanel } from "./SettingsPanel.js";

export function Toolbar() {
  const context = useStore((state) => state.context);
  const setMonth = useStore((state) => state.setMonth);
  const view = useStore((state) => state.view);
  const setView = useStore((state) => state.setView);
  const theme = useStore((state) => state.theme);
  const toggleTheme = useStore((state) => state.toggleTheme);
  const logout = useStore((state) => state.logout);

  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <header className="flex items-center justify-between gap-4 border-b border-node-border bg-node-bg px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="flex items-center gap-2">
          <img src="/logo.png" alt="" className="h-7 w-7" />
          <span className="text-lg font-semibold text-ink">Vulpari</span>
        </span>
        <LedgerMenu />
        <AddNodeMenu />
      </div>

      {settingsOpen ? <SettingsPanel onClose={() => setSettingsOpen(false)} /> : null}

      <div className="flex items-center gap-2">
        {/* Switch between the editable canvas, the runway, and the timeline. */}
        <div
          className="flex rounded-lg border border-node-border p-0.5"
          role="group"
          aria-label="View"
        >
          {(["canvas", "runway", "timeline"] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setView(option)}
              aria-pressed={view === option}
              className={`rounded-md px-3 py-1 text-sm capitalize transition-colors ${
                view === option ? "bg-accent text-canvas" : "text-ink-muted hover:text-ink"
              }`}
            >
              {option}
            </button>
          ))}
        </div>

        {/* Pick the month to model; each month shows its own one-offs. Arrows
            step a month at a time. */}
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => setMonth(addMonths(context.month, -1))}
            aria-label="Previous month"
            className="rounded-l-lg border border-r-0 border-node-border bg-canvas px-2 py-1.5 text-sm text-ink-muted hover:text-ink"
          >
            ‹
          </button>
          <input
            type="month"
            value={context.month}
            onChange={(event) => setMonth(event.target.value)}
            className="figure border-y border-node-border bg-canvas px-2 py-1.5 text-sm text-ink"
            aria-label="Month"
          />
          <button
            type="button"
            onClick={() => setMonth(addMonths(context.month, 1))}
            aria-label="Next month"
            className="rounded-r-lg border border-l-0 border-node-border bg-canvas px-2 py-1.5 text-sm text-ink-muted hover:text-ink"
          >
            ›
          </button>
        </div>

        <ExportMenu />

        <button
          type="button"
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          className="flex items-center gap-1.5 rounded-lg border border-node-border px-3 py-1.5 text-sm text-ink-muted hover:text-ink"
        >
          <Icon name={theme === "dark" ? "sun" : "moon"} size={16} />
          {theme === "dark" ? "Light" : "Dark"}
        </button>

        <button
          type="button"
          onClick={() => setSettingsOpen(true)}
          className="flex items-center gap-1.5 rounded-lg border border-node-border px-3 py-1.5 text-sm text-ink-muted hover:text-ink"
        >
          <Icon name="gear" size={16} />
          Account
        </button>

        <button
          type="button"
          onClick={logout}
          className="flex items-center gap-1.5 rounded-lg border border-node-border px-3 py-1.5 text-sm text-ink-muted hover:text-ink"
        >
          <Icon name="sign-out" size={16} />
          Sign out
        </button>
      </div>
    </header>
  );
}
